import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { Box, Container, Typography, Button, Paper, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, TextField } from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import ChatIcon from "../../../assets/images/AgentStar.svg";
import NewChatIcon from "../../../assets/icons/NewChatIcon";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  ReactFlowProvider,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";
import NodePalette from "./components/NodePalette";
import { CustomNode, LLMNode, KnowledgeBaseNode, ToolNode, PromptNode, CustomPromptNode, AgentNode, ParserNode } from "./components/CustomNodes";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { AGENT, KNOWLEDGE_BASE, MODELS } from "../../../Services/Endpoint";
import { Agent } from "../../../Shared-ui/Types/Agent";
import { KnowledgeBase } from "../../../Shared-ui/Types/KnowledgeBase";
import { useDispatch } from "react-redux";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { NotSelected } from "../../../Constant/Setting";
import { GetSession } from "../../../Shared-ui/utils/Auth/AuthHelpers";
import PlaygroundChat from "./components/PlaygroundChat";

// Debounce utility function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

const CreateAgent = () => {
  // Define node types with memoization
  const nodeTypes = useMemo(() => ({
    customNode: CustomNode,
    llmNode: LLMNode,
    knowledgeBaseNode: KnowledgeBaseNode,
    toolNode: ToolNode,
    promptNode: PromptNode,
    customPromptNode: CustomPromptNode,
    agentNode: AgentNode,
    parserNode: ParserNode,
  }), []);
  const { agentId } = useParams<{ agentId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Debounced node updates to reduce re-renders
  const debouncedSetNodes = useCallback(
    debounce((newNodes: Node[]) => {
      setNodes(newNodes);
    }, 100),
    [setNodes]
  );

  // Debounced edge updates to reduce re-renders
  const debouncedSetEdges = useCallback(
    debounce((newEdges: Edge[]) => {
      setEdges(newEdges);
    }, 100),
    [setEdges]
  );
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [agentData, setAgentData] = useState<Agent | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if target node already has a connection on the specific handle
  const hasExistingConnection = useCallback((targetNodeId: string, targetHandle: string) => {
    return edges.some((edge: any) =>
      edge.target === targetNodeId && edge.targetHandle === targetHandle
    );
  }, [edges]);

  // Connection validation function
  const isValidConnection = useCallback((connection: Connection) => {
    const sourceNode = nodes.find((node: any) => node.id === connection.source);
    const targetNode = nodes.find((node: any) => node.id === connection.target);

    if (!sourceNode || !targetNode) return false;

    const sourceCategory = sourceNode.data.category;
    const targetCategory = targetNode.data.category;
    const targetHandle = connection.targetHandle;

    // Check for single input restrictions (only for LLM, Prompt, and Parser nodes)
    const singleInputHandles = ['llm-input', 'prompt-input', 'parser-input'];
    if (singleInputHandles.includes(targetHandle!) && hasExistingConnection(connection.target!, targetHandle!)) {
      return false; // Already has a connection on this handle
    }

    // Define valid connections based on target handle
    switch (targetHandle) {
      case 'llm-input':
        // Only LLM nodes can connect to LLM input
        return sourceCategory === 'LLM Models';

      case 'knowledge-input':
        // Only Knowledge Base nodes can connect to Knowledge input
        return sourceCategory === 'Knowledge Bases';

      case 'tools-input':
        // Only Tool nodes can connect to Tools input
        return sourceCategory === 'Tools';

      case 'prompt-input':
        // Only Prompt nodes can connect to Prompt input
        return sourceCategory === 'Prompts';

      case 'parser-input':
        // Only Parser nodes can connect to Parser input
        return sourceCategory === 'Parser';

      case 'context-input':
        // Only Knowledge Base nodes can connect to Context input
        return sourceCategory === 'Knowledge Bases';

      default:
        return true; // Allow other connections
    }
  }, [nodes, hasExistingConnection]);

  const onConnect = useCallback(
    (params: Connection) => {
      // Validate connection before adding
      if (isValidConnection(params)) {
        setEdges((eds: any) => addEdge(params, eds));
      }
    },
    [setEdges, isValidConnection]
  );

  const onDeleteNode = useCallback(
    (nodes: Node[]) => {
      const nodeIds = nodes.map(node => node.id);
      setNodes((nds) => nds.filter((node) => !nodeIds.includes(node.id)));
      setEdges((eds) => eds.filter((edge) => !nodeIds.includes(edge.source) && !nodeIds.includes(edge.target)));
    },
    [setNodes, setEdges]
  );


  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);


  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data || !reactFlowInstance) return;

      try {
        const nodeTemplate = JSON.parse(data);

        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const newNode: Node = {
          id: `${nodeTemplate.id}-${Date.now()}`,
          type: getNodeType(nodeTemplate.category, nodeTemplate),
          position,
          data: {
            label: nodeTemplate.label,
            description: nodeTemplate.description,
            category: nodeTemplate.category,
            knowledgeBase: nodeTemplate.category === 'Knowledge Bases' ? undefined : nodeTemplate.knowledgeBase, // Don't pre-select KB for new nodes
            template: nodeTemplate.template, // System prompt always has template data
            tool: nodeTemplate.tool,
            provider: nodeTemplate.provider,
            models: nodeTemplate.models,
            apiKey: nodeTemplate.category === 'LLM Models' ? '' : undefined, // Add empty API key for new LLM Model nodes
            agentData: nodeTemplate.category === 'Agent' ? {
              show_tool_calls: false,
              is_shared: false,
              is_active: true,
              agent_metadata: {
                markdown: false,
                page_context_awareness: false,
                user_context_awareness: false,
                streaming: false,
                enable_zeasecure: false,
                zeasecure_api_key: '',
              }
            } : undefined,
            parserAttributes: nodeTemplate.category === 'Parser' ? [] : undefined,
          },
        };

        setNodes((nds: any) => nds.concat(newNode));
      } catch (error) {
      }
    },
    [reactFlowInstance, setNodes]
  );

  const getNodeType = (category: string, nodeTemplate?: any) => {
    switch (category) {
      case 'LLM Models':
        return 'llmNode';
      case 'Knowledge Bases':
        return 'knowledgeBaseNode';
      case 'Tools':
        return 'toolNode';
      case 'Prompts':
        // Check if it's a custom prompt
        if (nodeTemplate?.id === 'custom-prompt') {
          return 'customPromptNode';
        }
        return 'promptNode';
      case 'Agent':
        return 'agentNode';
      case 'Parser':
        return 'parserNode';
      default:
        return 'customNode';
    }
  };

  // Load agent data and convert to graph structure
  const loadAgentData = useCallback(async (id: string) => {
    try {
      setIsLoading(true);

      // Load agent data with React Flow data for editing
      const response = await BaseInstance.get(`${AGENT}/${id}?include_flow_data=true`);
      const agent: Agent = response.data;

      setAgentData(agent);
      setIsEditMode(true);

      // Restore React Flow diagram if saved data exists
      if (agent.react_flow_json && agent.react_flow_json.nodes && agent.react_flow_json.edges) {
        try {
          // Restore nodes and edges from saved data
          const restoredNodes = agent.react_flow_json.nodes.map((node: any) => {
            const restoredData = {
              ...node.data,
              // Ensure all required data properties are present
              label: node.data.label || 'Unnamed Node',
              category: node.data.category || 'custom',
              description: node.data.description || '',
            };

            // For LLM nodes, ensure provider and model data is properly restored
            if (node.type === 'llmNode') {
              // If the node has provider and model, ensure they're properly set
              if (node.data.provider && node.data.model) {
                restoredData.provider = node.data.provider;
                restoredData.model = node.data.model;
                restoredData.label = `${node.data.provider}:${node.data.model}`;
              } else {
                // If provider/model are missing from node data, try to extract from agent data
                if (agent.model && agent.model !== 'NotSelected') {
                  const [provider, model] = agent.model.includes(':')
                    ? agent.model.split(':')
                    : ['Unknown', agent.model];

                  restoredData.provider = provider;
                  restoredData.model = model;
                  restoredData.label = `${provider}:${model}`;
                  restoredData.description = `Model: ${agent.model}`;
                }
              }

              // If models array is missing, try to restore it
              if (!restoredData.models || restoredData.models.length === 0) {
                const fallbackModels = {
                  "OpenAI": ["gpt-4o", "gpt-4o-mini", "gpt-4", "gpt-3.5-turbo"],
                  "Anthropic": ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"],
                  "Google": ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-1.0-pro"]
                };
                const provider = restoredData.provider || 'OpenAI';
                restoredData.models = fallbackModels[provider as keyof typeof fallbackModels] || [restoredData.model || 'gpt-4o'];
              }

              // Ensure API key is restored
              if (!restoredData.apiKey && agent.api_key) {
                restoredData.apiKey = agent.api_key;
              }
            }

            // For Knowledge Base nodes, ensure knowledge base data is properly restored
            if (node.type === 'knowledgeBaseNode') {
              // If the node has knowledge base data, ensure it's properly set
              if (node.data.knowledgeBase) {
                restoredData.knowledgeBase = node.data.knowledgeBase;
                restoredData.label = node.data.knowledgeBase.name;
              }
              // Note: If knowledge base data is missing, the KnowledgeBaseNode component
              // will handle finding the correct KB from the agent's knowledge_base_ids
            }

            // For Agent nodes, ensure agentData is properly restored
            if (node.type === 'agentNode') {
              restoredData.agentData = agent;
              restoredData.agentName = agent.name;
            }

            // For Parser nodes, ensure parserAttributes are properly restored
            if (node.type === 'parserNode') {
              // Restore parser attributes from the saved node data
              if (node.data.parserAttributes) {
                restoredData.parserAttributes = node.data.parserAttributes;
              }
              // If parser attributes are missing from node data but exist in agent data,
              // try to restore them from the agent's output_parser
              else if (agent.output_parser && agent.output_parser.length > 0) {
                restoredData.parserAttributes = agent.output_parser;
              }
            }

            return {
              ...node,
              data: restoredData
            };
          });

          const restoredEdges = agent.react_flow_json.edges || [];

          // Synchronize KB nodes with agent.knowledge_base_ids to ensure classical/visual builder sync
          let syncedNodes = [...restoredNodes];
          let syncedEdges = [...restoredEdges];

          // Find the agent node to connect new KB nodes to
          const agentNode = syncedNodes.find((node: any) => node.type === 'agentNode');

          if (agentNode && agent.knowledge_base_ids) {
            // Get current KB node IDs from the flow
            const currentKbNodeIds = new Set(
              syncedNodes
                .filter((node: any) => node.type === 'knowledgeBaseNode')
                .map((node: any) => node.data.knowledgeBase?.id)
                .filter(Boolean)
            );

            // Get KB IDs from agent data
            const agentKbIds = new Set(agent.knowledge_base_ids);

            // 1. Remove orphaned KB nodes (exist in flow but not in knowledge_base_ids)
            const orphanedKbNodeIds: string[] = [];
            syncedNodes.forEach((node: any) => {
              if (node.type === 'knowledgeBaseNode') {
                const kbId = node.data.knowledgeBase?.id;
                if (kbId && !agentKbIds.has(kbId)) {
                  orphanedKbNodeIds.push(node.id);
                }
              }
            });

            if (orphanedKbNodeIds.length > 0) {
              // Remove orphaned KB nodes
              syncedNodes = syncedNodes.filter((node: any) => !orphanedKbNodeIds.includes(node.id));
              // Remove edges connected to orphaned KB nodes
              syncedEdges = syncedEdges.filter((edge: any) =>
                !orphanedKbNodeIds.includes(edge.source) && !orphanedKbNodeIds.includes(edge.target)
              );
            }

            // 2. Add missing KB nodes (exist in knowledge_base_ids but not in flow)
            const missingKbIds = Array.from(agentKbIds).filter(kbId => !currentKbNodeIds.has(kbId));

            if (missingKbIds.length > 0) {
              // Find the highest Y position of existing KB nodes to position new ones below
              const kbNodes = syncedNodes.filter((node: any) => node.type === 'knowledgeBaseNode');
              let startY = 200;
              if (kbNodes.length > 0) {
                const maxY = Math.max(...kbNodes.map((node: any) => node.position.y));
                startY = maxY + 100;
              }

              // Create placeholder nodes for missing KBs
              missingKbIds.forEach((kbId, index) => {
                const newKbNode: Node = {
                  id: `kb-${kbId}`,
                  type: 'knowledgeBaseNode',
                  position: { x: 100, y: startY + (index * 100) },
                  data: {
                    label: `Loading KB ${kbId}...`,
                    category: 'Knowledge Bases',
                    description: 'Loading knowledge base...',
                    knowledgeBase: {
                      id: kbId,
                      name: `Knowledge Base ${kbId}`,
                      description: `Knowledge Base ${kbId}`,
                      type: 'vector',
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                      created_by: agent.created_by || '',
                      is_active: true,
                    }
                  }
                };
                syncedNodes.push(newKbNode);

                // Create edge connecting new KB to agent
                const newEdge: Edge = {
                  id: `edge-kb-${kbId}`,
                  source: `kb-${kbId}`,
                  target: agentNode.id,
                  sourceHandle: 'output',
                  targetHandle: 'knowledge-input',
                };
                syncedEdges.push(newEdge);
              });

              // Load KB details asynchronously for new nodes
              setTimeout(async () => {
                try {
                  const kbPromises = missingKbIds.map(async (kbId) => {
                    try {
                      const kbResponse = await BaseInstance.get(`${KNOWLEDGE_BASE}/${kbId}`);
                      return { id: kbId, data: kbResponse.data };
                    } catch (error) {
                      return { id: kbId, data: null };
                    }
                  });

                  const knowledgeBases = await Promise.all(kbPromises);

                  // Update nodes with actual KB data
                  setNodes((prevNodes: any) =>
                    prevNodes.map((node: any) => {
                      if (node.type === 'knowledgeBaseNode') {
                        const kbData = knowledgeBases.find(kb => kb.id === node.data.knowledgeBase?.id);
                        if (kbData?.data) {
                          const lightweightKB = {
                            ...kbData.data,
                            documents: [] // Remove documents to prevent performance issues
                          };

                          return {
                            ...node,
                            data: {
                              ...node.data,
                              label: kbData.data.name,
                              description: kbData.data.description || `Knowledge Base ${kbData.id}`,
                              knowledgeBase: lightweightKB
                            }
                          };
                        }
                      }
                      return node;
                    })
                  );
                } catch (error) {
                  console.error('Failed to load knowledge base details:', error);
                }
              }, 100);
            }
          }

          // Set the synchronized flow data
          setNodes(syncedNodes);
          setEdges(syncedEdges);

          // If we have restored data, we can return early as the diagram is already loaded
          return;
        } catch (error) {
          console.warn('Failed to restore React Flow data, falling back to default conversion:', error);
        }
      }

      // Convert agent data to graph structure (fallback if no saved flow data)
      const graphNodes: Node[] = [];
      const graphEdges: Edge[] = [];

      // Create Agent node immediately for better UX
      const agentNode: Node = {
        id: `agent-${agent.id}`,
        type: 'agentNode',
        position: { x: 400, y: 200 },
        data: {
          label: agent.name,
          category: 'Agent',
          description: agent.description,
          agentName: agent.name,
          agentData: agent, // Pass the full agent data for settings
        }
      };
      graphNodes.push(agentNode);

      // Update nodes immediately to show the agent node
      setNodes(graphNodes);
      setEdges([]);

      // Create LLM node if model is specified (non-blocking)
      if (agent.model && agent.model !== 'NotSelected') {
        // Parse provider:model format
        const [provider, model] = agent.model.includes(':')
          ? agent.model.split(':')
          : ['Unknown', agent.model];

        // Use fallback models immediately to avoid blocking
        const fallbackModels = {
          "OpenAI": ["gpt-4o", "gpt-4o-mini", "gpt-4", "gpt-3.5-turbo"],
          "Anthropic": ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"],
          "Google": ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-1.0-pro"]
        };
        const availableModels = fallbackModels[provider as keyof typeof fallbackModels] || [model];

        const llmNode: Node = {
          id: `llm-${agent.id}`,
          type: 'llmNode',
          position: { x: 100, y: 100 },
          data: {
            label: agent.model,
            category: 'LLM Models',
            description: `Model: ${agent.model}`,
            provider: provider,
            model: model,
            models: availableModels,
            apiKey: agent.api_key || '',
          }
        };
        graphNodes.push(llmNode);

        // Connect LLM to Agent
        graphEdges.push({
          id: `edge-llm-${agent.id}`,
          source: `llm-${agent.id}`,
          target: `agent-${agent.id}`,
          sourceHandle: 'output',
          targetHandle: 'llm-input',
        });
      }

      // Create Knowledge Base nodes (lazy loading)
      if (agent.knowledge_base_ids && agent.knowledge_base_ids.length > 0) {
        // Add safety check for too many knowledge bases
        if (agent.knowledge_base_ids.length > 50) {
          throw new Error(`Too many knowledge bases (${agent.knowledge_base_ids.length}). Maximum allowed is 50.`);
        }

        // Create placeholder nodes immediately for better UX
        agent.knowledge_base_ids.forEach((kbId, index) => {
          const kbNode: Node = {
            id: `kb-${kbId}`,
            type: 'knowledgeBaseNode',
            position: { x: 100, y: 200 + (index * 100) },
            data: {
              label: `Loading KB ${kbId}...`,
              category: 'Knowledge Bases',
              description: 'Loading knowledge base...',
              knowledgeBase: {
                id: kbId,
                name: `Knowledge Base ${kbId}`,
                description: `Knowledge Base ${kbId}`,
                type: 'vector',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                created_by: agent.created_by || '',
                is_active: true,
              }
            }
          };
          graphNodes.push(kbNode);

          // Connect Knowledge Base to Agent
          graphEdges.push({
            id: `edge-kb-${kbId}`,
            source: `kb-${kbId}`,
            target: `agent-${agent.id}`,
            sourceHandle: 'output',
            targetHandle: 'knowledge-input',
          });
        });

        // Load knowledge base details asynchronously
        setTimeout(async () => {
          try {
            const kbPromises = agent.knowledge_base_ids.map(async (kbId) => {
              try {
                const kbResponse = await BaseInstance.get(`${KNOWLEDGE_BASE}/${kbId}`);
                return { id: kbId, data: kbResponse.data };
              } catch (error) {
                return { id: kbId, data: null };
              }
            });

            const knowledgeBases = await Promise.all(kbPromises);

            // Update nodes with actual data (create lightweight version without documents)
            setNodes((prevNodes: any) =>
              prevNodes.map((node: any) => {
                if (node.type === 'knowledgeBaseNode') {
                  const kbData = knowledgeBases.find(kb => kb.id === node.data.knowledgeBase.id);
                  if (kbData?.data) {
                    // Create lightweight version without documents to prevent performance issues
                    const lightweightKB = {
                      ...kbData.data,
                      documents: kbData.data.documents ? [] : [] // Remove documents to prevent performance issues
                    };

                    return {
                      ...node,
                      data: {
                        ...node.data,
                        label: kbData.data.name,
                        description: kbData.data.description || `Knowledge Base ${kbData.id}`,
                        knowledgeBase: lightweightKB
                      }
                    };
                  }
                }
                return node;
              })
            );
          } catch (error) {
            console.error('Failed to load knowledge base details:', error);
          }
        }, 100); // Small delay to let UI render first
      }

      // Create Tool nodes (lazy loading)
      if (agent.tool_ids && agent.tool_ids.length > 0) {
        // Add safety check for too many tools
        if (agent.tool_ids.length > 100) {
          throw new Error(`Too many tools (${agent.tool_ids.length}). Maximum allowed is 100.`);
        }

        // Create placeholder nodes immediately for better UX
        agent.tool_ids.forEach((toolId, index) => {
          const toolNode: Node = {
            id: `tool-${toolId}`,
            type: 'toolNode',
            position: { x: 100, y: 300 + (index * 100) },
            data: {
              label: `Loading Tool ${toolId}...`,
              category: 'Tools',
              description: 'Loading tool...',
              tool: {
                id: toolId,
                name: `Tool ${toolId}`,
                description: `Tool ${toolId}`,
                type: 'function',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                created_by: agent.created_by || '',
                is_active: true,
              }
            }
          };
          graphNodes.push(toolNode);

          // Connect Tool to Agent
          graphEdges.push({
            id: `edge-tool-${toolId}`,
            source: `tool-${toolId}`,
            target: `agent-${agent.id}`,
            sourceHandle: 'output',
            targetHandle: 'tools-input',
          });
        });

        // Load tool details asynchronously
        setTimeout(async () => {
          try {
            const toolPromises = agent.tool_ids.map(async (toolId) => {
              try {
                const toolResponse = await BaseInstance.get(`/tools/${toolId}`);
                return { id: toolId, data: toolResponse.data };
              } catch (error) {
                return { id: toolId, data: null };
              }
            });

            const tools = await Promise.all(toolPromises);

            // Update nodes with actual data
            setNodes((prevNodes: any) =>
              prevNodes.map((node: any) => {
                if (node.type === 'toolNode') {
                  const toolData = tools.find(tool => tool.id === node.data.tool.id);
                  if (toolData?.data) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        label: toolData.data.name,
                        description: toolData.data.description || `Tool: ${toolData.data.name}`,
                        tool: toolData.data
                      }
                    };
                  }
                }
                return node;
              })
            );
          } catch (error) {
            console.error('Failed to load tool details:', error);
          }
        }, 200); // Small delay to let UI render first
      }

      // Create Prompt node if instructions exist (lazy loading for large content)
      if (agent.instructions) {
        // Add safety check for extremely long instructions
        if (agent.instructions.length > 100000) { // 100KB limit
          throw new Error(`Instructions too long (${agent.instructions.length} characters). Maximum allowed is 100,000 characters.`);
        }

        const promptNode: Node = {
          id: `prompt-${agent.id}`,
          type: 'customPromptNode',
          position: { x: 100, y: 400 },
          data: {
            label: 'Custom Prompt',
            category: 'Prompts',
            description: 'Agent instructions',
            template: {
              id: 'custom-prompt',
              name: 'Custom Prompt',
              description: 'Agent instructions',
              category: 'custom',
              icon: null,
              tags: ['custom', 'agent-instructions'],
              popularity: 0,
              content: agent.instructions.length > 1000 ? '' : agent.instructions, // Load large content lazily
            }
          }
        };
        graphNodes.push(promptNode);

        // Connect Prompt to Agent
        graphEdges.push({
          id: `edge-prompt-${agent.id}`,
          source: `prompt-${agent.id}`,
          target: `agent-${agent.id}`,
          sourceHandle: 'output',
          targetHandle: 'prompt-input',
        });

        // Load large instruction content asynchronously
        if (agent.instructions.length > 1000) {
          setTimeout(() => {
            setNodes((prevNodes: any) =>
              prevNodes.map((node: any) => {
                if (node.id === `prompt-${agent.id}` && node.type === 'customPromptNode') {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      template: {
                        ...node.data.template,
                        content: agent.instructions
                      }
                    }
                  };
                }
                return node;
              })
            );
          }, 300); // Delay for large content
        }
      }

      // Create Parser node if output_parser exists
      if (agent.output_parser && agent.output_parser.length > 0) {
        const parserNode: Node = {
          id: `parser-${agent.id}`,
          type: 'parserNode',
          position: { x: 200, y: 500 },
          data: {
            label: 'Parser',
            category: 'Parser',
            description: 'Parse and extract structured data from content',
            parserAttributes: agent.output_parser,
          }
        };
        graphNodes.push(parserNode);

        // Connect Parser to Agent
        graphEdges.push({
          id: `edge-parser-${agent.id}`,
          source: `parser-${agent.id}`,
          target: `agent-${agent.id}`,
          sourceHandle: 'output',
          targetHandle: 'parser-input',
        });
      }

      // Update the graph with all nodes and edges
      setNodes(graphNodes);
      setEdges(graphEdges);

    } catch (error: any) {
      dispatch(ShowError(`Failed to load agent: ${error?.message || 'Unknown error'}`));
    } finally {
      setIsLoading(false);
    }
  }, [setNodes, setEdges]);

  // Load agent data when agentId is present
  useEffect(() => {
    if (agentId) {
      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          dispatch(ShowError('Agent loading timeout. Please try again.'));
          setIsLoading(false);
        }
      }, 30000); // 30 second timeout

      loadAgentData(agentId);

      return () => clearTimeout(timeoutId);
    }
  }, [agentId, loadAgentData, dispatch]);

  // Generate JSON structure for the current graph (returns object for API calls)
  const generateJsonStructure = useCallback(() => {
    const graphStructure = {
      nodes: nodes.map((node: any) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          label: node.data.label,
          category: node.data.category,
          description: node.data.description,
          // Include specific data based on node type
          ...(node.data.knowledgeBase && { knowledgeBase: node.data.knowledgeBase }),
          ...(node.data.tool && { tool: node.data.tool }),
          ...(node.data.template && { template: node.data.template }),
          ...(node.data.agentName && { agentName: node.data.agentName }),
          // Include LLM-specific data
          ...(node.data.provider && { provider: node.data.provider }),
          ...(node.data.model && { model: node.data.model }),
          ...(node.data.models && { models: node.data.models }),
          ...(node.data.apiKey && { apiKey: node.data.apiKey }),
          // Include Agent-specific data
          ...(node.data.agentData && { agentData: node.data.agentData }),
        }
      })),
      edges: edges.map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type,
        animated: edge.animated,
        style: edge.style,
      })),
      metadata: {
        totalNodes: nodes.length,
        totalEdges: edges.length,
        nodeTypes: Array.from(new Set(nodes.map((node: any) => node.type))),
        categories: Array.from(new Set(nodes.map((node: any) => node.data.category))),
        generatedAt: new Date().toISOString(),
      }
    };

    return graphStructure;
  }, [nodes, edges]);

  // Convert graph data to API format
  const convertGraphToAgentData = useCallback(() => {
    const agentNode = nodes.find((node: any) => node.type === 'agentNode');
    if (!agentNode) {
      throw new Error('No agent node found in the graph');
    }

    const nodeAgentData = agentNode.data.agentData || {};
    // Use agent name from node data instead of DOM query for better performance
    const agentName = agentNode.data.agentName || agentNode.data.label || 'Untitled Agent';
    // Use agent description from node data
    const agentDescription = agentNode.data.description || 'Agent created from visual flow';

    // Find connected nodes
    const llmNode = nodes.find((node: any) =>
      node.type === 'llmNode' &&
      edges.some((edge: any) => edge.source === node.id && edge.target === agentNode.id && edge.targetHandle === 'llm-input')
    );

    const knowledgeBaseNodes = nodes.filter((node: any) =>
      node.type === 'knowledgeBaseNode' &&
      edges.some((edge: any) => edge.source === node.id && edge.target === agentNode.id && edge.targetHandle === 'knowledge-input') &&
      node.data.knowledgeBase // Only include nodes that have a selected knowledge base
    );

    const toolNodes = nodes.filter((node: any) =>
      node.type === 'toolNode' &&
      edges.some((edge: any) => edge.source === node.id && edge.target === agentNode.id && edge.targetHandle === 'tools-input')
    );

    const promptNode = nodes.find((node: any) =>
      node.type === 'customPromptNode' &&
      edges.some((edge: any) => edge.source === node.id && edge.target === agentNode.id && edge.targetHandle === 'prompt-input') &&
      node.data.template?.content // Only include nodes that have content
    );

    const parserNode = nodes.find((node: any) =>
      node.type === 'parserNode' &&
      edges.some((edge: any) => edge.source === node.id && edge.target === agentNode.id && edge.targetHandle === 'parser-input')
    );

    // Build agent data
    const apiAgentData: Agent = {
      name: agentName,
      description: agentDescription,
      role: nodeAgentData.role || 'Document Agent',
      model: llmNode ? `${llmNode.data.provider}:${llmNode.data.model}` : NotSelected,
      api_key: llmNode ? (llmNode.data.apiKey || '') : (nodeAgentData.api_key || ''),
      instructions: promptNode ? (promptNode.data.template?.content || '') : '',
      is_active: nodeAgentData.is_active !== undefined ? nodeAgentData.is_active : true,
      is_shared: nodeAgentData.is_shared || false,
      show_tool_calls: nodeAgentData.show_tool_calls || false,
      show_reasoning: nodeAgentData.show_reasoning || true,
      reasoning_steps: nodeAgentData.reasoning_steps || false,
      tool_ids: toolNodes.map((node: any) => node.data.tool?.id).filter((id: any) => id && typeof id === 'string' && id.trim() !== ''),
      knowledge_base_ids: knowledgeBaseNodes.map((node: any) => node.data.knowledgeBase?.id).filter((id: any) => id && typeof id === 'string' && id.trim() !== ''),
      output_parser: parserNode ? parserNode.data.parserAttributes || [] : null,
      react_flow_json: generateJsonStructure(), // Include React Flow diagram data
      agent_metadata: {
        rag_settings: {
          add_references: true,
          search_knowledge: true,
        },
        streaming: nodeAgentData.agent_metadata?.streaming || false,
        enable_zeasecure: nodeAgentData.agent_metadata?.enable_zeasecure || false,
        zeasecure_api_key: nodeAgentData.agent_metadata?.zeasecure_api_key || '',
        markdown: nodeAgentData.agent_metadata?.markdown || false,
        page_context_awareness: nodeAgentData.agent_metadata?.page_context_awareness || false,
        user_context_awareness: nodeAgentData.agent_metadata?.user_context_awareness || false,
        voice_enabled: nodeAgentData.agent_metadata?.voice_enabled || false,
      },
      ...(isEditMode && agentData?.id && typeof agentData.id === 'string' && agentData.id.trim() !== '' && { id: agentData.id })
    };

    return apiAgentData;
  }, [nodes, edges, isEditMode, generateJsonStructure, agentData]);

  // Validate agent data
  const validateAgentData = useCallback((agent: Agent) => {
    const errors = [];

    if (!agent.name.trim()) {
      errors.push('Agent name is required');
    }

    if (!agent.description.trim()) {
      errors.push('Agent description is required');
    }

    if (!agent.role.trim()) {
      errors.push('Agent role is required');
    }

    if (agent.model === NotSelected) {
      errors.push('Model selection is required');
    }

    if (!agent.instructions.trim()) {
      errors.push('Instructions are required');
    }

    // API key is optional - removed mandatory validation to match classic builder behavior
    // if (!agent.api_key.trim()) {
    //   errors.push('API key is required');
    // }

    if (agent.agent_metadata.enable_zeasecure) {
      if (!agent.agent_metadata.zeasecure_api_key.trim()) {
        errors.push('ZeaSecure API key is required when ZeaSecure is enabled');
      } else if (agent.agent_metadata.zeasecure_api_key.trim().length !== 36) {
        errors.push('ZeaSecure API key must be 36 characters long');
      } else if (!agent.agent_metadata.zeasecure_api_key.trim().startsWith('zsk_')) {
        errors.push('ZeaSecure API key must start with "zsk_"');
      }
    }

    return errors;
  }, []);

  // Save agent (create or update)
  const saveAgent = useCallback(async () => {
    try {
      setIsSaving(true);
      dispatch(StartLoading());

      const agentData = convertGraphToAgentData();
      const validationErrors = validateAgentData(agentData);

      if (validationErrors.length > 0) {
        dispatch(ShowError(validationErrors.join(', ')));
        return;
      }

      let response;
      if (isEditMode) {
        // Update existing agent
        response = await BaseInstance.put(`${AGENT}/${agentData.id}`, agentData);
        dispatch(ShowSuccess('Agent updated successfully!'));
        // Stay on the same page after updating
      } else {
        // Create new agent
        response = await BaseInstance.post(AGENT, agentData);
        dispatch(ShowSuccess('Agent created successfully!'));
        // Navigate to edit page for the newly created agent
        navigate(`/agents/create-agent/${response.data.id}`);
      }
    } catch (error: any) {
      let errorMessage = 'Failed to save agent';

      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      dispatch(ShowError(errorMessage));
    } finally {
      setIsSaving(false);
      dispatch(StopLoading());
    }
  }, [convertGraphToAgentData, validateAgentData, isEditMode, dispatch, navigate]);

  return (
    <>
      <Container maxWidth={false} sx={{ py: 3, height: "calc(100vh - 81px)", px: 3 }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Breadcrumb */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              background: "white",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Breadcrumb
              data={[
                { name: "Agents", path: "/agents" },
                { name: isEditMode ? `Edit Agent: ${agentData?.name || agentId}` : "Create Agent", path: "" }
              ]}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={saveAgent}
                disabled={isSaving || isLoading}
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{ textTransform: "none" }}
              >
                {isSaving ? 'Saving...' : (isEditMode ? 'Update Agent' : 'Create Agent')}
              </Button>
            </Box>
          </Box>

          {/* Main Content - React Flow Editor */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              overflow: "hidden",
              border: 1,
              borderColor: "divider",
            }}
          >
            {/* Left Pane - Node Palette */}
            <NodePalette />

            {/* Center Pane - React Flow */}
            <Box sx={{ flex: 1, position: "relative" }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onInit={setReactFlowInstance}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onNodesDelete={onDeleteNode}
                  nodeTypes={nodeTypes}
                  isValidConnection={isValidConnection}
                  fitView={false}
                  onlyRenderVisibleElements={true}
                  nodesDraggable={true}
                  nodesConnectable={true}
                  panOnDrag={true}
                  zoomOnScroll={true}
                  zoomOnPinch={true}
                  preventScrolling={true}
                  maxZoom={1.5}
                  minZoom={0.5}
                  defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                  proOptions={{ hideAttribution: true }}
                  defaultEdgeOptions={{
                    animated: false,
                    style: { stroke: '#b1b1b7', strokeWidth: 1 }
                  }}
                  connectionLineStyle={{ stroke: '#b1b1b7', strokeWidth: 1 }}
                  nodeOrigin={[0.5, 0.5]}
                  edgeUpdaterRadius={10}
                  connectionRadius={20}
                >
                  <Controls />
                  <Background color="#aaa" gap={16} />
                </ReactFlow>
              </ReactFlowProvider>
            </Box>

            {/* Right Sidebar - Chatbot (Only in Edit Mode) */}
            {isEditMode && (
              <PlaygroundChat
                agentData={agentData}
                isEditMode={isEditMode}
              />
            )}
          </Box>
        </Box>
      </Container>

    </>
  );
};

export default CreateAgent;
